// ======================================================================================|
// MARK: Trial Eligibility (A7Y-483 Phase C, A7Y-502 env scoping)
//
// Types + helpers for trial-fraud prevention. A single shared module so a7y-api (CF
// Workers), a7y-functions (Node), admin UI, and backfill scripts all agree on the
// normalization rules, the doc-key format, and the record/denial shapes.
//
// IMPORTANT: `normalizeEmailForFraud` is ONLY for trial-fraud deduplication. Never
// call it for any other purpose — original email addresses must be preserved
// everywhere outside the fraud-dedup code path (login, email delivery, profile, etc).
//
// ENV SCOPING (A7Y-502): docs are keyed by `${emailNormalized}__${env}` so staging
// runs don't consume prod trial slots. Both TrialEligibility and
// TrialEligibilityOverrides use the same key format.

import { SELF_SERVE_TRIAL_DAYS } from './billing.js';

// ======================================================================================|
// MARK: Records

/**
 * Deployment environment used as part of the TrialEligibility doc key and stored
 * as a field for audit/filtering. Short form matches the CLI flag convention.
 */
export type TrialEligibilityEnv = 'prod' | 'staging';

/**
 * Build the Firestore doc id for a TrialEligibility / TrialEligibilityOverrides
 * document. Env-scoped so staging runs don't overwrite prod trial slots.
 *
 * Example: buildTrialEligibilityKey("alex@gmail.com", "prod") -> "alex@gmail.com__prod"
 */
export function buildTrialEligibilityKey(
  emailNormalized: string,
  env: TrialEligibilityEnv
): string {
  return `${emailNormalized}__${env}`;
}

/**
 * Persisted record that a given normalized email has consumed a trial slot.
 * Lives in the `TrialEligibility` Firestore collection, keyed by
 * `${emailNormalized}__${env}` (see `buildTrialEligibilityKey`).
 */
export interface TrialEligibilityRecord {
  /** Email as the user entered it; preserved verbatim for audit/support. */
  emailOriginal: string;
  /** Result of `normalizeEmailForFraud(emailOriginal)`. Combined with `env` to form the doc id. */
  emailNormalized: string;
  /** Deployment env — part of the doc key and stored for filtering. */
  env: TrialEligibilityEnv;
  /** Clerk user id at trial start, if known at write time. */
  userId: string | null;
  /** The org that consumed the trial slot. */
  orgId: string;
  /** ISO 8601 timestamp for when the trial started. */
  trialStartedAt: string;
  /** ISO 8601 stamped on customer.subscription.deleted (trial-expiration branch). */
  trialExpiredAt: string | null;
  /** ISO 8601 doc creation. */
  createdAt: string;
  /** ISO 8601 last update. */
  updatedAt: string;
}

/**
 * Admin-granted override that lets a normalized email start a fresh trial even
 * though a prior TrialEligibilityRecord exists. Lives in the
 * `TrialEligibilityOverrides` collection, keyed by `${emailNormalized}__${env}`
 * (see `buildTrialEligibilityKey`). Overrides are env-scoped — a staging
 * override does not unlock a prod trial.
 */
export interface TrialEligibilityOverride {
  /** The normalized email; combined with `env` to form the Firestore doc id. */
  emailNormalized: string;
  /** Deployment env — part of the doc key and stored for filtering. */
  env: TrialEligibilityEnv;
  /** Human-readable reason — surfaced in the admin audit trail. */
  reason: string;
  /** Clerk user id of the admin who granted the override. */
  grantedBy: string;
  /** Admin email at grant time (for audit display). */
  grantedByEmail: string;
  /** ISO 8601 grant timestamp. */
  grantedAt: string;
  /** ISO 8601 revocation timestamp (null/undefined if still active). */
  revokedAt?: string | null;
  /** Clerk user id of the admin who revoked the override. */
  revokedBy?: string | null;
}

// ======================================================================================|
// MARK: Denial error shape

export const TRIAL_ELIGIBILITY_DENIAL_CODES = [
  'PRIOR_TRIAL_CONSUMED',
  'INVITE_BLOCKED_PRIOR_TRIAL',
] as const;

export type TrialEligibilityDenialCode = (typeof TRIAL_ELIGIBILITY_DENIAL_CODES)[number];

/**
 * Structured error body returned to clients (HTTP 403) when eligibility is denied.
 * Stable contract — client uses `code` for branching, `message` for display copy.
 */
export interface TrialEligibilityDenial {
  code: TrialEligibilityDenialCode;
  /** Plain-language, user-facing copy. */
  message: string;
  /** Always "support@apptonomy.ai" — hard-coded so UI doesn't need to know. */
  supportEmail: string;
  emailNormalized: string;
  /** The orgId that was being created/joined (for support triage). */
  orgIdAttempted?: string;
}

export const TRIAL_ELIGIBILITY_SUPPORT_EMAIL = 'support@apptonomy.ai';

// ======================================================================================|
// MARK: Second-org-during-trial denial (A7Y-480)
//
// Distinct from TrialEligibilityDenial — this isn't about prior-trial-consumed
// fraud, it's about gating org creation while the user already has an active
// (trialing, no paid sibling) organization. The user must set up billing on
// their existing org before spinning up another one.

/**
 * Structured error body returned as HTTP 403 from `POST /orgs` when the
 * authenticated user already owns/belongs to an org that is currently
 * `trialing` and has no paid sibling on their account.
 */
export interface SecondOrgDuringTrialDenial {
  code: 'SECOND_ORG_DURING_TRIAL';
  /** Plain-language, user-facing copy. */
  message: string;
  /** Always "support@apptonomy.ai" — mirrors TrialEligibilityDenial shape. */
  supportEmail: string;
  /** The existing trialing org the user already has (for deep-linking the "Set up billing" CTA). */
  trialOrgId?: string;
}

/**
 * Read-only precheck used by billing UIs before starting paid checkout. This
 * mirrors the real checkout gate but never throws, so the client can decide
 * whether to show "start free trial" copy without duplicating server rules.
 */
export type BillingTrialEligibilityDenial = TrialEligibilityDenial | SecondOrgDuringTrialDenial;

export interface BillingTrialEligibilityCheckResult {
  allowed: boolean;
  /** Mirrors the self-serve trial constant so clients can render accurate copy. */
  trialDays: number;
  /** Present only when `allowed === false`. */
  denial?: BillingTrialEligibilityDenial;
}

export const DEFAULT_BILLING_TRIAL_ELIGIBILITY: BillingTrialEligibilityCheckResult = {
  allowed: true,
  trialDays: SELF_SERVE_TRIAL_DAYS,
};

// ======================================================================================|
// MARK: normalizeEmailForFraud
//
// Rules (applied in order, idempotently):
//   1) lowercase the entire address
//   2) strip anything after `+` on the local part (plus-addressing)
//   3) strip dots from the local part
//   4) canonicalize `googlemail.com` -> `gmail.com` on the domain
//
// Examples:
//   "Alex+staging@gmail.com"    -> "alex@gmail.com"
//   "a.l.e.x@googlemail.com"    -> "alex@gmail.com"
//   "USER@corp.COM"             -> "user@corp.com"
//
// Inputs that don't contain exactly one `@` are lowercased and returned as-is (we
// never throw on malformed input — callers treat the normalized string as opaque).

export function normalizeEmailForFraud(email: string): string {
  if (!email || typeof email !== 'string') return '';

  const lowered = email.trim().toLowerCase();

  const atIdx = lowered.indexOf('@');
  if (atIdx === -1 || atIdx !== lowered.lastIndexOf('@')) {
    // Malformed — return lowercased form; caller's dedup will still be consistent.
    return lowered;
  }

  let local = lowered.slice(0, atIdx);
  let domain = lowered.slice(atIdx + 1);

  // Rule 2: strip plus-addressing
  const plusIdx = local.indexOf('+');
  if (plusIdx !== -1) local = local.slice(0, plusIdx);

  // Rule 3: strip dots from local part
  local = local.split('.').join('');

  // Rule 4: googlemail -> gmail
  if (domain === 'googlemail.com') domain = 'gmail.com';

  return `${local}@${domain}`;
}
